import { ethers } from "ethers";
import CrownABI from "../abi/Crown.json";

// Contract address
export const contractAddress = "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03";

export async function connectToContract(provider) {
  try {
    if (!provider) {
      throw new Error("Provider is not available.");
    }
    const signer = await provider.getSigner();
    const contractWithSigner = new ethers.Contract(contractAddress, CrownABI, signer);
    console.log("Connected to contract at address:", contractWithSigner.address);
    console.log("Signer:", signer.address);
    console.log("Contract instance:", contractWithSigner);
    return contractWithSigner;
  } catch (error) {
    console.error("Error connecting to contract:", error.message);
    throw error;
  }
}

export async function listenToEvents(provider, callback) {
  try {
    if (!provider) {
      throw new Error("Provider is not available.");
    }
    const contractWithProvider = new ethers.Contract(contractAddress, CrownABI, provider);
    console.log("Setting up listeners for all Crown contract events at contract address:", contractWithProvider.address);
    console.log("Provider network:", await provider.getNetwork());
    // Log ABI event availability
    const events = [
      "Transfer", "Staked", "Unstaked", "RewardsClaimed",
      "UserLoanCreated", "UserLoanActivated", "UserLoanRepaid", "UserLoanLiquidated", "UserLoanCancelled",
      "LoanCreated", "LoanRepaid", "CollateralLiquidated",
      "ValidatorAdded", "ValidatorRemoved", "RewardsWithdrawn",
      "TransactionFeeRateUpdated", "UseOraclePriceUpdated", "CrwPriceFeedUpdated",
      "TokensBought", "TokensSold", "TokensBoughtWithERC20", "TokensSoldForERC20",
      "TokenAdded", "TokenRemoved", "TokenUpdated", "RewardPoolFeeTransferred",
      "ValidatorFeesDistributed", "Approval",
      "DailyRewardRateUpdated", "EthTransferAttempted", "DailyInterestRateUpdated"
    ];
    events.forEach(event => {
      console.log(`Contract ABI includes ${event}:`, !!contractWithProvider.interface.getEvent(event));
    });

    // Listener for Transfer
    contractWithProvider.on("Transfer", async (from, to, value, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("Transfer event received:", {
          from, to, value: ethers.formatEther(value), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "Transfer", data: { from, to, value }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing Transfer event:", error.message);
      }
    });

    // Listener for Staked
    contractWithProvider.on("Staked", async (staker, amountStaked, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("Staked event received:", {
          staker, amount: ethers.formatEther(amountStaked), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "Staked", data: { staker, amount: amountStaked }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing Staked event:", error.message);
      }
    });

    // Listener for Unstaked
    contractWithProvider.on("Unstaked", async (staker, amountUnstaked, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("Unstaked event received:", {
          staker, amount: ethers.formatEther(amountUnstaked), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "Unstaked", data: { staker, amount: amountUnstaked }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing Unstaked event:", error.message);
      }
    });

    // Listener for RewardsClaimed
    contractWithProvider.on("RewardsClaimed", async (staker, rewardsClaimed, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("RewardsClaimed event received:", {
          staker, amount: ethers.formatEther(rewardsClaimed), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "RewardsClaimed", data: { staker, amount: rewardsClaimed }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing RewardsClaimed event:", error.message);
      }
    });

    // Listener for UserLoanCreated
    contractWithProvider.on("UserLoanCreated", async (lender, borrower, loanAmount, interestRate, loanDuration, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("UserLoanCreated event received:", {
          lender, borrower: borrower || "None", loanAmount: ethers.formatEther(loanAmount), interestRate, loanDuration, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "UserLoanCreated", data: { lender, borrower, loanAmount, interestRate, loanDuration }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing UserLoanCreated event:", error.message);
      }
    });

    // Listener for UserLoanActivated
    contractWithProvider.on("UserLoanActivated", async (borrower, collateralAmount, loanAmount, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("UserLoanActivated event received:", {
          borrower, collateralAmount: ethers.formatEther(collateralAmount), loanAmount: ethers.formatEther(loanAmount), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "UserLoanActivated", data: { borrower, collateralAmount, loanAmount }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing UserLoanActivated event:", error.message);
      }
    });

    // Listener for UserLoanRepaid
    contractWithProvider.on("UserLoanRepaid", async (borrower, lender, totalRepaymentAmount, remainingCollateral, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("UserLoanRepaid event received:", {
          borrower, lender, totalRepaymentAmount: ethers.formatEther(totalRepaymentAmount), remainingCollateral: ethers.formatEther(remainingCollateral), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "UserLoanRepaid", data: { borrower, lender, totalRepaymentAmount, remainingCollateral }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing UserLoanRepaid event:", error.message);
      }
    });

    // Listener for UserLoanLiquidated
    contractWithProvider.on("UserLoanLiquidated", async (borrower, lender, totalCollateral, lenderShare, ownerShare, rewardPoolShare, liquidationTimestamp, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("UserLoanLiquidated event received:", {
          borrower, lender, totalCollateral: ethers.formatEther(totalCollateral), lenderShare: ethers.formatEther(lenderShare), ownerShare: ethers.formatEther(ownerShare), rewardPoolShare: ethers.formatEther(rewardPoolShare), liquidationTimestamp, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "UserLoanLiquidated", data: { borrower, lender, totalCollateral, lenderShare, ownerShare, rewardPoolShare, liquidationTimestamp }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing UserLoanLiquidated event:", error.message);
      }
    });

    // Listener for UserLoanCancelled
    contractWithProvider.on("UserLoanCancelled", async (lender, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("UserLoanCancelled event received:", {
          lender, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "UserLoanCancelled", data: { lender }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing UserLoanCancelled event:", error.message);
      }
    });

    // Listener for LoanCreated
    contractWithProvider.on("LoanCreated", async (borrower, collateralWei, loanAmount, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("LoanCreated event received:", {
          borrower, collateralWei: ethers.formatEther(collateralWei), loanAmount: ethers.formatEther(loanAmount), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "LoanCreated", data: { borrower, collateralWei, loanAmount }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing LoanCreated event:", error.message);
      }
    });

    // Listener for LoanRepaid
    contractWithProvider.on("LoanRepaid", async (borrower, collateralReturned, totalRepayment, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("LoanRepaid event received:", {
          borrower, collateralReturned: ethers.formatEther(collateralReturned), totalRepayment: ethers.formatEther(totalRepayment), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "LoanRepaid", data: { borrower, collateralReturned, totalRepayment }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing LoanRepaid event:", error.message);
      }
    });

    // Listener for CollateralLiquidated
    contractWithProvider.on("CollateralLiquidated", async (borrower, collateralWei, reserveShareInWei, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("CollateralLiquidated event received:", {
          borrower, collateralWei: ethers.formatEther(collateralWei), reserveShareInWei: ethers.formatEther(reserveShareInWei), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "CollateralLiquidated", data: { borrower, collateralWei, reserveShareInWei }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing CollateralLiquidated event:", error.message);
      }
    });

    // Listener for ValidatorAdded
    contractWithProvider.on("ValidatorAdded", async (validatorAddress, stakedAmount, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("ValidatorAdded event received:", {
          validatorAddress, stakedAmount: ethers.formatEther(stakedAmount), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "ValidatorAdded", data: { validatorAddress, stakedAmount }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing ValidatorAdded event:", error.message);
      }
    });

    // Listener for ValidatorRemoved
    contractWithProvider.on("ValidatorRemoved", async (validatorAddress, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("ValidatorRemoved event received:", {
          validatorAddress, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "ValidatorRemoved", data: { validatorAddress }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing ValidatorRemoved event:", error.message);
      }
    });

    // Listener for RewardsWithdrawn
    contractWithProvider.on("RewardsWithdrawn", async (validator, amount, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("RewardsWithdrawn event received:", {
          validator, amount: ethers.formatEther(amount), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "RewardsWithdrawn", data: { validator, amount }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing RewardsWithdrawn event:", error.message);
      }
    });

    // Listener for TransactionFeeRateUpdated
    contractWithProvider.on("TransactionFeeRateUpdated", async (oldRate, newRate, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TransactionFeeRateUpdated event received:", {
          oldRate, newRate, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TransactionFeeRateUpdated", data: { oldRate, newRate }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TransactionFeeRateUpdated event:", error.message);
      }
    });

    // Listener for UseOraclePriceUpdated
    contractWithProvider.on("UseOraclePriceUpdated", async (owner, newStatus, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("UseOraclePriceUpdated event received:", {
          owner, newStatus, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "UseOraclePriceUpdated", data: { owner, newStatus }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing UseOraclePriceUpdated event:", error.message);
      }
    });

    // Listener for CrwPriceFeedUpdated
    contractWithProvider.on("CrwPriceFeedUpdated", async (oldFeed, newFeed, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("CrwPriceFeedUpdated event received:", {
          oldFeed, newFeed, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "CrwPriceFeedUpdated", data: { oldFeed, newFeed }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing CrwPriceFeedUpdated event:", error.message);
      }
    });

    // Listener for TokensBought
    contractWithProvider.on("TokensBought", async (buyer, ethAmountInWei, tokensBought, feeInTokens, ownerFee, rewardPoolFee, validatorsFee, contractEthBalance, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokensBought event received:", {
          buyer, ethAmountInWei: ethers.formatEther(ethAmountInWei), tokensBought: ethers.formatEther(tokensBought), feeInTokens: ethers.formatEther(feeInTokens), ownerFee: ethers.formatEther(ownerFee), rewardPoolFee: ethers.formatEther(rewardPoolFee), validatorsFee: ethers.formatEther(validatorsFee), contractEthBalance: ethers.formatEther(contractEthBalance), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokensBought", data: { buyer, ethAmountInWei, tokensBought, feeInTokens, ownerFee, rewardPoolFee, validatorsFee, contractEthBalance }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokensBought event:", error.message);
      }
    });

    // Listener for TokensSold
    contractWithProvider.on("TokensSold", async (seller, tokenAmount, ethReceived, feeInTokens, ownerFee, rewardPoolFee, validatorsFee, contractEthBalance, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokensSold event received:", {
          seller, tokenAmount: ethers.formatEther(tokenAmount), ethReceived: ethers.formatEther(ethReceived), feeInTokens: ethers.formatEther(feeInTokens), ownerFee: ethers.formatEther(ownerFee), rewardPoolFee: ethers.formatEther(rewardPoolFee), validatorsFee: ethers.formatEther(validatorsFee), contractEthBalance: ethers.formatEther(contractEthBalance), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokensSold", data: { seller, tokenAmount, ethReceived, feeInTokens, ownerFee, rewardPoolFee, validatorsFee, contractEthBalance }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokensSold event:", error.message);
      }
    });

    // Listener for TokensBoughtWithERC20
    contractWithProvider.on("TokensBoughtWithERC20", async (buyer, token, tokenAmount, tokensBought, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokensBoughtWithERC20 event received:", {
          buyer, token, tokenAmount: ethers.formatEther(tokenAmount), tokensBought: ethers.formatEther(tokensBought), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokensBoughtWithERC20", data: { buyer, token, tokenAmount, tokensBought }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokensBoughtWithERC20 event:", error.message);
      }
    });

    // Listener for TokensSoldForERC20
    contractWithProvider.on("TokensSoldForERC20", async (seller, token, tokenAmount, erc20Received, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokensSoldForERC20 event received:", {
          seller, token, tokenAmount: ethers.formatEther(tokenAmount), erc20Received: ethers.formatEther(erc20Received), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokensSoldForERC20", data: { seller, token, tokenAmount, erc20Received }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokensSoldForERC20 event:", error.message);
      }
    });

    // Listener for TokenAdded
    contractWithProvider.on("TokenAdded", async (token, priceFeed, tokenDecimals, heartbeat, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokenAdded event received:", {
          token, priceFeed, tokenDecimals, heartbeat, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokenAdded", data: { token, priceFeed, tokenDecimals, heartbeat }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokenAdded event:", error.message);
      }
    });

    // Listener for TokenRemoved
    contractWithProvider.on("TokenRemoved", async (token, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokenRemoved event received:", {
          token, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokenRemoved", data: { token }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokenRemoved event:", error.message);
      }
    });

    // Listener for TokenUpdated
    contractWithProvider.on("TokenUpdated", async (token, newPriceFeed, newHeartbeat, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("TokenUpdated event received:", {
          token, newPriceFeed, newHeartbeat, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "TokenUpdated", data: { token, newPriceFeed, newHeartbeat }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing TokenUpdated event:", error.message);
      }
    });

    // Listener for RewardPoolFeeTransferred
    contractWithProvider.on("RewardPoolFeeTransferred", async (from, feeAmount, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("RewardPoolFeeTransferred event received:", {
          from, feeAmount: ethers.formatEther(feeAmount), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "RewardPoolFeeTransferred", data: { from, feeAmount }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing RewardPoolFeeTransferred event:", error.message);
      }
    });

    // Listener for ValidatorFeesDistributed
    contractWithProvider.on("ValidatorFeesDistributed", async (totalValidatorFee, individualValidatorShare, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("ValidatorFeesDistributed event received:", {
          totalValidatorFee: ethers.formatEther(totalValidatorFee), individualValidatorShare: ethers.formatEther(individualValidatorShare), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "ValidatorFeesDistributed", data: { totalValidatorFee, individualValidatorShare }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing ValidatorFeesDistributed event:", error.message);
      }
    });

    // Listener for Approval
    contractWithProvider.on("Approval", async (owner, spender, amount, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("Approval event received:", {
          owner, spender, amount: ethers.formatEther(amount), blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "Approval", data: { owner, spender, amount }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing Approval event:", error.message);
      }
    });

    // Listener for DailyRewardRateUpdated
    contractWithProvider.on("DailyRewardRateUpdated", async (oldRate, newRate, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("DailyRewardRateUpdated event received:", {
          oldRate, newRate, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "DailyRewardRateUpdated", data: { oldRate, newRate }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing DailyRewardRateUpdated event:", error.message);
      }
    });

    // Listener for EthTransferAttempted
    contractWithProvider.on("EthTransferAttempted", async (to, amount, success, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("EthTransferAttempted event received:", {
          to, amount: ethers.formatEther(amount), success, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "EthTransferAttempted", data: { to, amount, success }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing EthTransferAttempted event:", error.message);
      }
    });

    // Listener for DailyInterestRateUpdated
    contractWithProvider.on("DailyInterestRateUpdated", async (newRate, eventData) => {
      try {
        const block = await provider.getBlock(eventData.blockNumber);
        const timestamp = block.timestamp;
        console.log("DailyInterestRateUpdated event received:", {
          newRate, blockNumber: eventData.blockNumber, transactionHash: eventData.transactionHash,
        });
        callback({ name: "DailyInterestRateUpdated", data: { newRate }, transactionHash: eventData.transactionHash, blockNumber: eventData.blockNumber, timestamp });
      } catch (error) {
        console.error("Error processing DailyInterestRateUpdated event:", error.message);
      }
    });

    return () => {
      console.log("Cleaning up listeners for all Crown contract events at contract address:", contractWithProvider.address);
      events.forEach(event => contractWithProvider.removeAllListeners(event));
    };
  } catch (error) {
    console.error("Error setting up event listener:", error.message);
    throw error;
  }
}

export async function getSupportedTokens(provider) {
  try {
    if (!provider) {
      throw new Error("Provider is not available.");
    }
    const contract = new ethers.Contract(contractAddress, CrownABI, provider);
    console.log("Fetching supported tokens for contract:", contract.address);
    const tokens = await contract.getSupportedTokens();
    return tokens;
  } catch (error) {
    console.error("Error fetching supported tokens:", error.message);
    return [];
  }
}